/**
 * HarperJobCaseFile — the case body for `harper_job` work items: one Harper
 * job run's record. Registered in caseBody.ts (one line) so the SAME body
 * renders on the Work page and inside the runs table's dialog.
 *
 * The body fetches its own run detail (GET /api/workers/harper/runs/:id,
 * keyed by the item's sourceRef.runId) rather than widening the work-items
 * API — the run row (stages, findings, failure) is the engine's record and
 * its route is where it lives.
 *
 * In reading order:
 *   1 · the delivery warning band, when the Update never reached its reader
 *       (the amber honesty case, with the retry verb), and the safe-mode note
 *       when a step fell back to the plain form;
 *   2 · the Update itself — what went out, or would have gone out;
 *   3 · the stages panel (what happened when);
 *   4 · "How this was written", collapsed: what was read, the Approach, every
 *       change's placement and reason, the check, each step.
 *
 * A run still in progress is re-read every few seconds, so the page follows
 * it from stage to stage.
 */

import { useCallback, useEffect, useState } from "react";

import { jobFailureCopy } from "@yagni/shared";
import { Button, Chip, HonestyNotice } from "../ui/index.js";
import { captureUiError } from "../sentry.js";
import {
  HARPER_RUN_DELIVERY_FAILED_BAND,
  HARPER_RUN_DELIVERY_RETRY,
  HARPER_RUN_FILING_TITLE,
  HARPER_RUN_NO_DELIVERY_NOTE,
  HARPER_RUN_RETRY_FAILED,
  HARPER_RUN_RETRY_STARTED,
  HARPER_RUN_STAGES_TITLE,
} from "../workers/copy.js";
import { harperRunStatusLabel, harperRunStatusTone } from "../workers/harperRunStatus.js";
import {
  retryHarperRun,
  type HarperRunDetail,
  type HarperRunStageEntry,
} from "../workers/useWorkers.js";
import type { CaseFileProps } from "./caseBodyProps.js";
import { HarperUpdateView, HowItWasWritten, degradedSentences } from "./harperUpdate.js";

/** How often a run in progress is re-read. */
export const HARPER_RUN_POLL_MS = 5000;

interface StageRecord {
  completedAt?: string;
  output?: unknown;
}

/** A stage's state as the panel prints it. */
const STAGE_STATE_WORD: Record<HarperRunStageEntry["status"], string> = {
  done: "done",
  degraded: "fell back",
  failed: "failed",
  running: "running",
  pending: "not run",
};

/**
 * The stages a detail with no `stageList` implies. The engine's own list wins
 * whenever it is there.
 */
function legacyStageList(
  stages: Record<string, StageRecord>,
  failure: { stage?: string } | null,
): HarperRunStageEntry[] {
  return ["gather", "dossiers", "editor", "writer", "critic", "deliver"].map((id) => {
    const record = stages[id];
    const done = Boolean(record && typeof record === "object");
    return {
      id,
      label: id,
      status: failure?.stage === id ? "failed" : done ? "done" : "pending",
      completedAt: record?.completedAt ?? null,
    };
  });
}

/**
 * Why the run stopped, in a person's words. A typed code reads its sentence;
 * a legacy row with no code keeps the reason it recorded.
 */
function failureSentence(failure: { reason?: string; code?: string } | null): string | null {
  if (!failure) return null;
  if (failure.code) return jobFailureCopy(failure.code).sentence;
  return failure.reason || null;
}

/** The stages panel: what happened when, with the failing stage's reason. */
function StagesPanel({
  stageList,
  failure,
  retryable,
  nextStep,
  retrying,
  onRetry,
}: {
  stageList: HarperRunStageEntry[];
  failure: { stage?: string; reason?: string; code?: string } | null;
  retryable: boolean;
  /** Said in place of the retry button when a retry is not the way forward. */
  nextStep: string | null;
  retrying: boolean;
  onRetry: () => void;
}) {
  const reason = failureSentence(failure);
  const failingStage = stageList.find((stage) => stage.status === "failed") ?? null;
  return (
    <section className="hrun-stages" data-testid="hrun-stages">
      <h3 className="hrun-stages-title">{HARPER_RUN_STAGES_TITLE}</h3>
      <ul className="hrun-stages-list">
        {stageList.map((stage) => {
          const failing = stage.status === "failed";
          return (
            <li
              key={stage.id}
              className={"hrun-stage" + (failing ? " hrun-stage-failed" : "")}
              data-testid={`hrun-stage-${stage.id}`}
            >
              <span className="hrun-stage-name">{stage.label}</span>
              <span className="hrun-stage-state">
                {stage.status === "degraded" && stage.code
                  ? `fell back: ${jobFailureCopy(stage.code).phrase}`
                  : STAGE_STATE_WORD[stage.status]}
              </span>
              {stage.completedAt && (
                <span className="hrun-stage-at">
                  {new Date(stage.completedAt).toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </span>
              )}
              {failing && reason && <span className="hrun-stage-reason">{reason}</span>}
            </li>
          );
        })}
      </ul>
      {retryable ? (
        <Button
          variant="secondary"
          size="sm"
          disabled={retrying}
          onClick={onRetry}
          data-testid="hrun-retry-button"
        >
          {retrying
            ? "Retrying…"
            : `Retry from ${failingStage ? failingStage.label.toLowerCase() : "the first incomplete stage"}`}
        </Button>
      ) : (
        nextStep && (
          <p className="hrun-stages-next" data-testid="hrun-next-step">
            {nextStep}
          </p>
        )
      )}
    </section>
  );
}

/**
 * The copy for a stopped run's code: a hold reads as capacity even when its
 * row predates codes; a run with no failure has none.
 */
function stoppedCopyOf(run: HarperRunDetail["run"]): ReturnType<typeof jobFailureCopy> | null {
  if (run.status === "held") return jobFailureCopy(run.failure?.code ?? "capacity.no_capacity");
  if (run.failure) return jobFailureCopy(run.failure.code);
  return null;
}

/** The delivery warning's tail: the code's sentence, else the recorded reason. */
function deliveryFailureSuffix(failure: { reason?: string; code?: string } | null): string {
  if (failure?.code) return `: ${jobFailureCopy(failure.code).sentence}`;
  if (failure?.reason) return `: Slack delivery failed: ${failure.reason}.`;
  return ".";
}

function windowSentence(run: HarperRunDetail["run"]): string {
  const start = new Date(run.windowStart);
  const end = new Date(run.windowEnd);
  const fmt = (d: Date): string =>
    Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return `${fmt(start)} → ${fmt(end)}`;
}

export function HarperJobCaseFile({
  item,
  onRetrySettled,
}: CaseFileProps & { onRetrySettled?: () => void }) {
  const sourceRef = (item.sourceRef ?? {}) as { runId?: unknown; jobId?: unknown };
  const runId = typeof sourceRef.runId === "string" ? sourceRef.runId : null;
  const [detail, setDetail] = useState<HarperRunDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retryNote, setRetryNote] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const read = useCallback(
    async (quiet: boolean): Promise<void> => {
      if (!runId) return;
      if (!quiet) setLoading(true);
      try {
        const res = await fetch(`/api/workers/harper/runs/${encodeURIComponent(runId)}`, { credentials: "include" });
        if (!res.ok) throw new Error(`run read failed: ${res.status}`);
        setDetail((await res.json()) as HarperRunDetail);
        setMissing(false);
      } catch (err) {
        captureUiError(err, {
          area: "Harper",
          operation: "harper_run_detail_read",
          severity: "warning",
          expected: true,
          runId,
        });
        // A quiet re-read that fails keeps what the page already shows.
        if (!quiet) {
          setDetail(null);
          setMissing(true);
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [runId],
  );

  useEffect(() => {
    if (!runId) {
      setDetail(null);
      setMissing(true);
      setLoading(false);
      return;
    }
    void read(false);
  }, [runId, read]);

  // Follow a run in progress from stage to stage.
  const inProgress = detail?.run.status === "in_progress";
  useEffect(() => {
    if (!inProgress) return;
    const timer = setInterval(() => void read(true), HARPER_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [inProgress, read]);

  async function retry(): Promise<void> {
    if (!runId) return;
    setRetrying(true);
    const result = await retryHarperRun(runId);
    setRetrying(false);
    setRetryNote(result.ok ? HARPER_RUN_RETRY_STARTED : (result.error ?? HARPER_RUN_RETRY_FAILED));
    if (result.ok) {
      // Re-read the run; the surface above refreshes its table.
      await read(false);
      onRetrySettled?.();
    }
  }

  if (loading) {
    return (
      <div className="hrun-case" data-testid="hrun-case-loading">
        <p className="wrk-empty">Loading the run…</p>
      </div>
    );
  }

  if (missing || !detail) {
    return (
      <div className="hrun-case" data-testid="hrun-case-missing">
        <p className="wrk-empty">
          This run's record could not be read back. It may have been deleted with its job.
        </p>
      </div>
    );
  }

  const run = detail.run;
  const job = detail.job;
  const stages = (run.stages ?? {}) as Record<string, StageRecord>;
  const update = run.update ?? null;
  const report = run.report ?? null;
  const fellBack = report?.quality.safeMode ? degradedSentences(report) : [];
  const deliveryFailed = run.status === "delivery_failed";
  // The one action the stopped run's code offers. A hold resumes on its own
  // and a delivery error is fixed at its channel, so neither offers Retry:
  // the panel says what happens next instead.
  const stoppedCopy = stoppedCopyOf(run);
  const stopped =
    deliveryFailed || run.status === "failed" || run.status === "needs_attention" || run.status === "held";
  const retryable =
    stopped && run.status !== "held" && (stoppedCopy === null || stoppedCopy.action === "retry");
  const nextStep = stopped && !retryable && stoppedCopy ? stoppedCopy.nextStep : null;
  const stageList = run.stageList ?? legacyStageList(stages, run.failure);

  return (
    <div className="hrun-case" data-testid="hrun-case">
      {/* 1 · the delivery warning — the product telling on itself */}
      {deliveryFailed && (
        <HonestyNotice className="hrun-delivery-warning" data-testid="hrun-delivery-warning">
          {HARPER_RUN_DELIVERY_FAILED_BAND}
          {deliveryFailureSuffix(run.failure)}
          {retryable && (
            <button
              type="button"
              className="hrun-retry-link"
              disabled={retrying}
              onClick={() => void retry()}
              data-testid="hrun-retry"
            >
              {retrying ? "Retrying…" : HARPER_RUN_DELIVERY_RETRY}
            </button>
          )}
        </HonestyNotice>
      )}
      {retryNote && (
        <p className="hrun-retry-note" data-testid="hrun-retry-note">
          {retryNote}
        </p>
      )}
      {fellBack.length > 0 && (
        <HonestyNotice className="hrun-safe-mode">
          <span data-testid="hrun-safe-mode">{fellBack.join(" ")}</span>
        </HonestyNotice>
      )}
      {report?.approach.conflicts.map((conflict) => (
        <p key={conflict} className="hrun-conflict" data-testid="hrun-conflict">
          {conflict}
        </p>
      ))}

      {/* 2 · the filing */}
      <section className="hrun-filing" data-testid="hrun-filing">
        <h3 className="hrun-filing-title">{HARPER_RUN_FILING_TITLE}</h3>
        {!update && <p className="hrun-filing-headline">{item.title}</p>}
        <p className="hrun-filing-window">
          <Chip tone={harperRunStatusTone(run.status)}>
            {run.status === "in_progress" && run.currentStage
              ? `${run.currentStage.label} · step ${run.currentStage.step} of ${run.currentStage.of}`
              : harperRunStatusLabel(run.status)}
          </Chip>{" "}
          {windowSentence(run)}
        </p>
        {job && !job.delivery && (
          <p className="hrun-no-delivery" data-testid="hrun-no-delivery">
            {HARPER_RUN_NO_DELIVERY_NOTE}
          </p>
        )}
        {update ? (
          <HarperUpdateView update={update} />
        ) : run.status === "nothing_to_file" ? (
          <p className="hrun-filing-none">A quiet window: nothing met this job's bar.</p>
        ) : run.status === "in_progress" ? (
          <p className="hrun-filing-none">The Update is still being written.</p>
        ) : (
          <p className="hrun-filing-none">
            The run stopped before it wrote an Update.{" "}
            {retryable && (
              <button type="button" className="hrun-retry-link" onClick={() => void retry()}>
                Retry
              </button>
            )}
          </p>
        )}
        {run.storyId && (
          <a
            href={`/desks/${encodeURIComponent(run.deskId)}/stories/${encodeURIComponent(run.storyId)}`}
            className="hrun-story-link"
          >
            Story →
          </a>
        )}
      </section>

      {/* 3 · the stages panel */}
      <StagesPanel
        stageList={stageList}
        failure={run.failure}
        retryable={retryable}
        nextStep={nextStep}
        retrying={retrying}
        onRetry={() => void retry()}
      />

      {/* 4 · how it was written (collapsed) */}
      {report && <HowItWasWritten report={report} />}
    </div>
  );
}
